import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Users, Package, Truck, DollarSign, RotateCcw } from "lucide-react";
import { useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface AnalyticsData {
  overview: {
    totalUsers: number;
    activeLoads: number;
    availableTrucks: number;
    monthlyRevenue: number;
  };
  userBreakdown: {
    shippers: number;
    carriers: number;
    brokers: number;
  };
  membershipBreakdown: {
    free: number;
    paid: number;
  };
  growth: {
    users: { month: string; count: number }[];
  };
}

interface DemoStatusData {
  accountsExist: boolean;
  demoAccountCount: number;
  lastResetAt: string | null;
}

export default function AdminDashboard() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [passwordsResult, setPasswordsResult] = useState<Array<{ email: string; password: string }> | null>(null);

  const { data: analytics, isLoading, error } = useQuery<AnalyticsData>({
    queryKey: ["/api/admin/analytics"],
  });

  const { data: demoStatus } = useQuery<DemoStatusData>({
    queryKey: ["/api/admin/demo-data-status"],
  });

  const resetDemoData = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/reset-demo-data", {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/demo-data-status"] });
      setResetDialogOpen(false);
      toast({
        title: "Demo data reset",
        description: "All demo scenarios have been wiped and repopulated successfully.",
      });
    },
    onError: () => {
      setResetDialogOpen(false);
      toast({
        title: "Reset failed",
        description: "Could not reset demo data. Check server logs for details.",
        variant: "destructive",
      });
    },
  });

  const resetDemoPasswordsMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/reset-demo-passwords", {});
      return res.json() as Promise<{ success: boolean; accounts: Array<{ email: string; password: string }> }>;
    },
    onSuccess: (data) => {
      setPasswordsResult(data.accounts);
    },
    onError: () => {
      toast({
        title: "Password reset failed",
        description: "Could not reset demo passwords. Check server logs for details.",
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-lg text-muted-foreground">Loading analytics...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-lg text-destructive mb-2">Access Denied</div>
          <div className="text-sm text-muted-foreground">You do not have permission to access the admin panel.</div>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-lg text-muted-foreground">No analytics data available</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-wide" data-testid="text-admin-title">Admin Dashboard</h1>
        <p className="text-muted-foreground">Platform analytics and insights</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-total-users">{analytics.overview.totalUsers}</div>
            <p className="text-xs text-muted-foreground">
              {analytics.membershipBreakdown.paid} paid, {analytics.membershipBreakdown.free} free
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Loads</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-active-loads">{analytics.overview.activeLoads}</div>
            <p className="text-xs text-muted-foreground">Available for carriers</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Available Trucks</CardTitle>
            <Truck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-available-trucks">{analytics.overview.availableTrucks}</div>
            <p className="text-xs text-muted-foreground">Ready for loads</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-monthly-revenue">${analytics.overview.monthlyRevenue}</div>
            <p className="text-xs text-muted-foreground">
              From {analytics.membershipBreakdown.paid} paid members
            </p>
          </CardContent>
        </Card>

      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>User Growth</CardTitle>
            <CardDescription>New users over time</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={analytics.growth.users}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="month" 
                  className="text-xs"
                  tickFormatter={(value) => {
                    const date = new Date(value + "-01");
                    return date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
                  }}
                />
                <YAxis className="text-xs" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: "hsl(var(--card))", 
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "0.5rem"
                  }}
                  labelFormatter={(value) => {
                    const date = new Date(value + "-01");
                    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="count" 
                  stroke="hsl(var(--primary))" 
                  fill="hsl(var(--primary))"
                  fillOpacity={0.2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>User Distribution</CardTitle>
            <CardDescription>Breakdown by user type</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Shippers</span>
                <span className="text-sm text-muted-foreground" data-testid="text-shippers-count">{analytics.userBreakdown.shippers}</span>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div 
                  className="h-full bg-primary" 
                  style={{ 
                    width: `${(analytics.userBreakdown.shippers / analytics.overview.totalUsers) * 100}%` 
                  }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Carriers</span>
                <span className="text-sm text-muted-foreground" data-testid="text-carriers-count">{analytics.userBreakdown.carriers}</span>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div 
                  className="h-full bg-primary" 
                  style={{ 
                    width: `${(analytics.userBreakdown.carriers / analytics.overview.totalUsers) * 100}%` 
                  }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Brokers</span>
                <span className="text-sm text-muted-foreground" data-testid="text-brokers-count">{analytics.userBreakdown.brokers}</span>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div 
                  className="h-full bg-primary" 
                  style={{ 
                    width: `${(analytics.userBreakdown.brokers / analytics.overview.totalUsers) * 100}%` 
                  }}
                />
              </div>
            </div>

            <div className="pt-4 border-t space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Free Members</span>
                <span className="text-sm text-muted-foreground" data-testid="text-free-count">{analytics.membershipBreakdown.free}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Paid Members</span>
                <span className="text-sm text-muted-foreground" data-testid="text-paid-count">{analytics.membershipBreakdown.paid}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
          <div>
            <CardTitle>Sales Demo Data</CardTitle>
            <CardDescription>
              Manage the pre-populated demo scenarios for the three demo accounts (demo.shipper, demo.carrier, demo.broker @fillthetrucks.com).
              Resetting wipes all transactional data for those accounts and repopulates 8 realistic shipment scenarios plus additional marketplace listings.
              User profile records are never deleted.
            </CardDescription>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="destructive"
              onClick={() => setResetDialogOpen(true)}
              disabled={resetDemoData.isPending}
              data-testid="button-reset-demo-data"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset Demo Data
            </Button>
            <Button
              variant="outline"
              onClick={() => { setPasswordsResult(null); resetDemoPasswordsMutation.mutate(); }}
              disabled={resetDemoPasswordsMutation.isPending}
              data-testid="button-reset-demo-passwords"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              {resetDemoPasswordsMutation.isPending ? "Resetting passwords…" : "Reset Demo Passwords"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground" data-testid="text-last-reset">
            {demoStatus?.lastResetAt ? (
              <>
                Last reset:{" "}
                {new Date(demoStatus.lastResetAt).toLocaleString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </>
            ) : (
              "No reset recorded yet"
            )}
          </p>
          {passwordsResult && (
            <div className="rounded-md border p-4 space-y-2 bg-muted/40">
              <p className="text-sm font-semibold">New demo account passwords — save these now:</p>
              {passwordsResult.map((acct) => (
                <div key={acct.email} className="text-sm font-mono" data-testid={`text-demo-password-${acct.email}`}>
                  <span className="text-muted-foreground">{acct.email}</span>
                  {" — "}
                  <span className="font-bold select-all">{acct.password}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Demo Data</DialogTitle>
            <DialogDescription>
              This will permanently delete all loads, truck postings, messages, threads, and ratings belonging to the three demo accounts, then repopulate them with fresh demo scenarios. Demo user profile records will not be touched. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setResetDialogOpen(false)}
              disabled={resetDemoData.isPending}
              data-testid="button-cancel-reset"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => resetDemoData.mutate()}
              disabled={resetDemoData.isPending}
              data-testid="button-confirm-reset"
            >
              {resetDemoData.isPending ? "Resetting…" : "Yes, Reset Demo Data"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
